export type SessionCacheOptions<T> = {
  key: string;
  version: number;
  isValidValue: (value: unknown) => value is T;
};

type SessionCacheEntry<T> = {
  version: number;
  value: T;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const readSessionCache = <T>({
  key,
  version,
  isValidValue,
}: SessionCacheOptions<T>): T | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;

    const entry: unknown = JSON.parse(raw);
    if (!isRecord(entry) || entry.version !== version || !isValidValue(entry.value)) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    return entry.value;
  } catch {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Session storage is unavailable; there is no cache to clear.
    }
    return null;
  }
};

export const writeSessionCache = <T>(key: string, version: number, value: T): void => {
  if (typeof window === "undefined") return;

  try {
    const entry: SessionCacheEntry<T> = { version, value };
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Session storage is optional; callers continue with their in-memory data.
  }
};
